// The page behind a standing room link (/r/<workspace>?k=…). Before the host starts, it says when the
// next session is, shows the files if the host allows it, and checks every few seconds; once a session
// is live it becomes that session's join page.
import { useEffect, useState } from 'react';
import { api, ApiError, type MeetingInfo } from '../api';
import { Page } from '../components/Chrome';
import { longDate } from '../components/Calendar';
import { formatSize, openVersion, type FileEntry } from '../files';
import { T, t } from '../tenant';
import { baseName, contents, crumbs } from '../tree';
import { MeetingPage } from './MeetingPage';

interface RoomInfo {
  workspace: { id: string; title: string };
  live: MeetingInfo | null;
  filesBetween: boolean;
  next: { date: string; time: string | null; title: string } | null;
}

export function RoomPage({ workspaceId, roomKey }: { workspaceId: string; roomKey: string | null }) {
  const [info, setInfo] = useState<RoomInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const q = `k=${encodeURIComponent(roomKey ?? '')}`;

  useEffect(() => {
    if (!roomKey) return setError('This link is incomplete. Ask for it again.');
    let stopped = false;
    const poll = async () => {
      try {
        const r = await api<RoomInfo>(`/api/r/${workspaceId}/info?${q}`);
        if (!stopped) setInfo(r);
      } catch (err) {
        if (!stopped && err instanceof ApiError && err.status === 404) setError((err as Error).message);
      }
    };
    void poll();
    // Waiting for the host to start: check every 5 s; stop once the session page takes over.
    const timer = setInterval(() => {
      if (!stopped) void poll();
    }, 5000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [workspaceId, roomKey, q]);

  if (info?.live) return <MeetingPage meetingId={info.live.id} link={null} room={{ workspaceId, key: roomKey! }} />;

  return (
    <Page title={info?.workspace.title ?? T.meeting}>
      {error ? (
        <div className="card">
          <h1 className="h1">Cannot open this link</h1>
          <p>{error}</p>
        </div>
      ) : !info ? (
        <div className="spinner" aria-label="Loading" />
      ) : (
        <div className="card list" data-testid="room-waiting">
          <h1 className="h1">{info.workspace.title}</h1>
          <div className="notice">
            The {t.meeting} has not started yet. Keep this page open: it opens by itself when it starts.
          </div>
          {info.next && (
            <div data-testid="room-next">
              <span className="muted small">Next {t.meeting}</span>
              <div>
                <strong>{longDate(info.next.date)}</strong>
                {info.next.time && <strong> · {info.next.time}</strong>} · {info.next.title}
              </div>
            </div>
          )}
          {info.filesBetween && <RoomFiles workspaceId={workspaceId} query={q} title={info.workspace.title} />}
        </div>
      )}
    </Page>
  );
}

function RoomFiles({ workspaceId, query, title }: { workspaceId: string; query: string; title: string }) {
  const [data, setData] = useState<{ files: FileEntry[]; folders: string[] } | null>(null);
  const [folder, setFolder] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api<{ files: FileEntry[]; folders: string[] }>(`/api/r/${workspaceId}/files?${query}`).then(setData, (e: Error) => setError(e.message));
  }, [workspaceId, query]);
  if (!data) return error ? <div className="error">{error}</div> : null;
  const here = folder === '' || data.folders.includes(folder) ? folder : '';
  const view = contents(data.files, data.folders, here);
  const open = (versionId: string, download: boolean) => openVersion(`/api/r/${workspaceId}/files/${versionId}?${query}`, download).catch((e: Error) => setError(e.message));
  return (
    <div className="list" data-testid="room-files">
      <h2 className="h2" style={{ margin: 0 }}>
        Files
      </h2>
      {error && <div className="error">{error}</div>}
      <nav className="crumbs" aria-label="Folder">
        {crumbs(here, title).map((c, i) => (
          <span key={c.path} className="row" style={{ gap: 2 }}>
            {i > 0 && <span className="sep">/</span>}
            <button type="button" onClick={() => setFolder(c.path)}>
              {c.label}
            </button>
          </span>
        ))}
      </nav>
      {view.folders.map((p) => (
        <div key={p} className="list-item folder-row" onClick={() => setFolder(p)} data-testid="room-folder">
          <span className="icon" aria-hidden>
            📁
          </span>
          <strong style={{ flex: 1 }}>{baseName(p)}</strong>
        </div>
      ))}
      {view.folders.length === 0 && view.files.length === 0 && <div className="muted small">No files here yet.</div>}
      {view.files.map((f) =>
        f.current ? (
          <div key={f.id} className="list-item" data-testid="room-file">
            <div style={{ flex: 1, minWidth: 140 }}>
              <div>{baseName(f.name)}</div>
              <div className="muted small">{formatSize(f.current.size)}</div>
            </div>
            {f.inline && (
              <button className="btn sm" onClick={() => open(f.current!.id, false)}>
                Open
              </button>
            )}
            <button className="btn sm" onClick={() => open(f.current!.id, true)}>
              Download
            </button>
          </div>
        ) : null,
      )}
    </div>
  );
}
